import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ArrowLeft, Phone, MessageCircle, MessageSquare, Mail, Flame,
  MapPin, ArrowRight, Bolt, StickyNote, Handshake, XCircle, ChevronRight,
} from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { useToast } from "src/components/ui/toast"
import { fetchLead, fetchLeadTimeline, convertLeadToDeal } from "src/features/leads/api/leadDetailApi"
import { placeCall, dialViaMobile } from "src/features/integrations/api/callsApi"
import { LeadDocuments } from "src/features/leads/components/LeadDocuments"
import { NotesPanel } from "src/features/notes/NotesPanel"
import { TasksPanel } from "src/features/tasks/components/TasksPanel"
import { FollowUpsPanel } from "src/features/followups/components/FollowUpsPanel"
import { TagEntitySection } from "src/features/tags/components/TagEntitySection"
import { LeadSiteVisitsPanel } from "src/features/site_visits/components/LeadSiteVisitsPanel"
import { useAuth } from "src/contexts/AuthContext"
import { useNetworkStatus } from "src/contexts/NetworkStatusContext"
import { useChannelProviders } from "src/features/integrations/hooks/useChannelProviders"
import { ProviderPicker } from "src/features/integrations/components/ProviderPicker"
import { SendMessageDrawer } from "src/features/leads/components/SendMessageDrawer"

const BASE_TABS = ["Overview", "Activity", "Follow-ups", "Tasks", "Notes", "Documents"]

const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", contacted: "bg-blue-100 text-blue-700",
  interested: "bg-violet-100 text-violet-700", site_visit: "bg-indigo-100 text-indigo-700",
  negotiation: "bg-amber-100 text-amber-700", converted: "bg-green-100 text-green-700",
  lost: "bg-red-100 text-red-700",
}

const ACTIVITY_ICON = {
  status_changed: { Icon: ArrowRight, cls: "bg-amber-50 text-amber-700" },
  deal_created: { Icon: Bolt, cls: "bg-blue-50 text-blue-700" },
  deal_won: { Icon: Bolt, cls: "bg-green-50 text-green-700" },
  deal_lost: { Icon: Bolt, cls: "bg-red-50 text-red-700" },
  deal_stage_changed: { Icon: ArrowRight, cls: "bg-slate-50 text-slate-600" },
  booking_created: { Icon: MapPin, cls: "bg-indigo-50 text-indigo-700" },
  channel_partner_assigned: { Icon: Handshake, cls: "bg-teal-50 text-teal-700" },
  channel_partner_removed: { Icon: XCircle, cls: "bg-red-50 text-red-700" },
  note: { Icon: StickyNote, cls: "bg-slate-50 text-slate-600" },
  message_sent: { Icon: MessageCircle, cls: "bg-emerald-50 text-emerald-700" },
  message_received: { Icon: MessageSquare, cls: "bg-sky-50 text-sky-700" },
}

function initials(name = "") {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?"
}

function activityText(a) {
  const m = a.metadata || {}
  switch (a.activity_type) {
    case "status_changed": return `Status changed from ${m.from} to ${m.to}`
    case "deal_created": return `Deal created on ${m.pipeline || "pipeline"} (${m.stage || "stage"})`
    case "deal_stage_changed": return `Deal moved ${m.from || "?"} → ${m.to}`
    case "deal_won": return "Deal marked won"
    case "deal_lost": return `Deal lost${a.notes ? `: ${a.notes}` : ""}`
    case "booking_created": return `Booking created for unit ${m.unit}`
    case "message_sent": return `${(m.channel || "Message")[0].toUpperCase()}${(m.channel || "essage").slice(1)} message sent`
    case "message_received": return `${(m.channel || "Message")[0].toUpperCase()}${(m.channel || "essage").slice(1)} message received${m.text ? `: ${m.text}` : ""}`
    default: return a.notes || a.activity_type
  }
}

// Audio player + transcript for a call recording, shown inline under its
// timeline entry. Capture method sets quality expectations; transcription
// status reflects the async pipeline (skipped when no provider is configured).
function CallRecordingBlock({ recording }) {
  const mins = recording.duration_seconds
    ? `${Math.floor(recording.duration_seconds / 60)}:${String(recording.duration_seconds % 60).padStart(2, "0")}`
    : null
  const captureLabel = recording.capture_method === "oem_system" ? "Both parties" : "Mic only"
  const status = recording.transcription_status

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs text-slate-500">
        <span className="rounded bg-slate-200 px-1.5 py-0.5 font-medium text-slate-600">{captureLabel}</span>
        {mins && <span>{mins}</span>}
      </div>
      {recording.audio_url && (
        <audio controls preload="none" src={recording.audio_url} className="w-full" />
      )}
      <div className="mt-2">
        {status === "done" && recording.transcript ? (
          <p className="whitespace-pre-wrap text-sm text-slate-700">{recording.transcript}</p>
        ) : status === "processing" ? (
          <p className="text-xs italic text-slate-400">Transcribing…</p>
        ) : status === "failed" ? (
          <p className="text-xs italic text-red-400">Transcription failed</p>
        ) : status === "skipped" ? (
          <p className="text-xs italic text-slate-400">Transcription not configured</p>
        ) : (
          <p className="text-xs italic text-slate-400">Transcription pending</p>
        )}
      </div>
    </div>
  )
}

// The sent/received message content, collapsed by default and revealed on
// click. Body comes from the linked MessageDelivery (see leads#timeline).
function MessageBlock({ message }) {
  const [open, setOpen] = useState(false)
  const hasBody = !!(message.subject || message.body)
  if (!hasBody) return null

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
      >
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        {open ? "Hide message" : "View message"}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {message.subject && (
            <p className="mb-1 text-sm font-medium text-slate-800">{message.subject}</p>
          )}
          <p className="whitespace-pre-wrap text-sm text-slate-700">{message.body}</p>
        </div>
      )}
    </div>
  )
}

export default function LeadDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { can } = useAuth()
  const { isOnline } = useNetworkStatus()
  const [lead, setLead] = useState(null)
  const [activities, setActivities] = useState([])
  const [tab, setTab] = useState("Activity")

  const TABS = can("site_visits", "read")
    ? [...BASE_TABS.slice(0, 3), "Site Visits", ...BASE_TABS.slice(3)]
    : BASE_TABS

  useEffect(() => {
    fetchLead(id).then(setLead).catch(() => toast({ variant: "destructive", title: "Could not load lead" }))
    fetchLeadTimeline(id).then(setActivities).catch(() => { })
  }, [id])

  async function convert() {
    try {
      const { deal } = await convertLeadToDeal(id)
      toast({ title: "Converted to deal", description: "Opening the deal…" })
      if (deal?.id) navigate(`/deals/${deal.id}`)
      else fetchLeadTimeline(id).then(setActivities)
    } catch (err) {
      toast({ variant: "destructive", title: "Could not convert", description: err.response?.data?.errors?.[0] })
    }
  }

  const callProviders = useChannelProviders("call")
  const [calling, setCalling] = useState(false)
  const [messageChannel, setMessageChannel] = useState(null) // "whatsapp" | "sms" | "email"

  // No voice provider configured → push the number to the user's phone and let
  // its native dialer take over.
  async function dialOnPhone() {
    try {
      const { device } = await dialViaMobile(id)
      toast({
        title: "Opening dialer on your phone…",
        description: device ? `Sent to ${device}.` : "Check your phone to place the call.",
      })
    } catch (err) {
      const code = err.response?.data?.code
      if (code === "no_device") {
        toast({ title: "Install the Clozdl mobile app to continue", description: "We couldn't find a signed-in phone to dial from." })
      } else {
        toast({
          variant: "destructive",
          title: "Couldn't reach your phone",
          description: err.response?.data?.error || "Please try again.",
        })
      }
    }
  }

  async function callLead() {
    if (calling) return
    setCalling(true)
    try {
      if (callProviders.none) {
        await dialOnPhone()
        return
      }
      await placeCall(id, { provider: callProviders.hasChoice ? callProviders.preferred : undefined })
      toast({
        title: "Connecting your call…",
        description: "Your phone will ring first, then we dial the lead. The call is logged on the timeline.",
      })
    } catch (err) {
      const errorMsg = err.response?.data?.error || ""
      if (
        errorMsg.includes("No calling provider configured") ||
        errorMsg.includes("is not configured for calls")
      ) {
        await dialOnPhone()
      } else {
        toast({
          variant: "destructive",
          title: "Couldn't place call",
          description: errorMsg || "Check the calling provider in Settings → Integrations.",
        })
      }
    } finally {
      setCalling(false)
    }
  }

  if (!lead) return <AppShell><div className="p-8 text-sm text-slate-400">Loading…</div></AppShell>

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        <button onClick={() => navigate("/leads")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to leads
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700">{initials(lead.name)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold text-slate-900">{lead.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[lead.status] || "bg-slate-100 text-slate-700"}`}>{lead.status}</span>
              {lead.score != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                  <Flame className="h-3 w-3" /> {lead.score}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-sm text-slate-500">Lead #{lead.id} · {lead.project_interest || "—"} · via {lead.platform || lead.source || "—"}</div>
          </div>
          <div className="flex gap-1.5">
            {/* Click-to-call: rings the agent first, then bridges to the lead. */}
            <button
              onClick={callLead}
              disabled={calling || !isOnline || lead.communication_actions?.call === false}
              title={
                !isOnline
                  ? "You're offline"
                  : lead.communication_actions?.call === false
                    ? "Lead has opted out of calls"
                    : "Call this lead"
              }
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Phone className={`h-4 w-4 ${calling ? "animate-pulse text-emerald-600" : ""}`} />
            </button>
            {/* Only shown when the org has 2+ voice providers enabled. */}
            {callProviders.hasChoice && (
              <ProviderPicker
                providers={callProviders.providers}
                value={callProviders.preferred}
                onChange={callProviders.remember}
                label="Call via"
              />
            )}
            {[
              { channel: "whatsapp", Icon: MessageCircle, needs: "phone", hoverCls: "hover:border-green-300 hover:bg-green-50 hover:text-green-700", verb: "WhatsApp", optOut: "Lead has opted out of WhatsApp" },
              { channel: "sms", Icon: MessageSquare, needs: "phone", hoverCls: "hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700", verb: "Text", optOut: "Lead has opted out of texts" },
              { channel: "email", Icon: Mail, needs: "email", hoverCls: "hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700", verb: "Email", optOut: "Lead has opted out of emails" },
            ].map(({ channel, Icon, needs, hoverCls, verb, optOut }) => {
              const hasContact = needs === "email" ? !!lead.email : !!lead.phone
              const optedOut = lead.communication_actions?.[channel] === false
              const disabled = !hasContact || optedOut
              return (
                <button
                  key={channel}
                  onClick={() => setMessageChannel(channel)}
                  disabled={disabled}
                  title={
                    optedOut ? optOut
                      : !hasContact ? `No ${needs === "email" ? "email address" : "phone number"}`
                        : `${verb} this lead`
                  }
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors ${hoverCls} disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-transparent disabled:hover:text-slate-500`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              )
            })}
          </div>
        </div>

        {/* Tags */}
        <TagEntitySection
          taggableType="Lead"
          taggableId={lead.id}
          initialTags={lead.tags || []}
          variant="inline"
          onChanged={() => fetchLead(id).then(setLead).catch(() => { })}
        />

        {/* Tabs */}
        <div className="flex gap-5 border-b border-slate-200 text-sm">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`-mb-px border-b-2 py-2 ${tab === t ? "border-slate-900 font-medium text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[230px_1fr]">
          {/* Details rail */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-xs text-slate-400">Details</div>
            <dl className="space-y-2.5 text-sm">
              <Detail k="Phone" v={lead.phone} />
              <Detail k="Email" v={lead.email} />
              <Detail k="Budget" v={lead.budget_range} />
              <Detail k="Source" v={[lead.source, lead.utm_source].filter(Boolean).join(" · ")} />
              {lead.channel_partner && (
                <Detail k="Channel Partner" v={`${lead.channel_partner.name} (${lead.channel_partner.referral_code || "no code"})`} />
              )}
              <Detail k="Owner" v={lead.assigned_to?.full_name} />
            </dl>
            {lead.deal ? (
              <button onClick={() => navigate(`/deals/${lead.deal.id}`)} className="mt-4 w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100">
                View Deal
              </button>
            ) : !["converted", "lost"].includes(lead.status) ? (
              <button onClick={convert} disabled={!isOnline} title={!isOnline ? "You're offline" : undefined} className="mt-4 w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40">
                Convert to deal
              </button>
            ) : null}
          </div>

          {/* Tab content */}
          <div>
            {tab === "Activity" && (
              <div className="flex flex-col gap-2">
                {activities.length === 0 && <Empty label="No activity yet" />}
                {activities.map((a) => {
                  const meta = ACTIVITY_ICON[a.activity_type] || { Icon: Bolt, cls: "bg-slate-50 text-slate-600" }
                  const Icon = meta.Icon
                  return (
                    <div key={a.id} className="flex gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.cls}`}><Icon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800">{activityText(a)}</div>
                        <div className="mt-0.5 text-xs text-slate-400">{a.user?.full_name || "System"} · {a.happened_at ? new Date(a.happened_at).toLocaleString() : ""}</div>
                        {a.recording && <CallRecordingBlock recording={a.recording} />}
                        {a.message && <MessageBlock message={a.message} />}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {tab === "Overview" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
                <p className="leading-relaxed">{lead.notes || "No overview notes for this lead yet."}</p>
              </div>
            )}
            {tab === "Site Visits" && <LeadSiteVisitsPanel lead={lead} />}
            {tab === "Follow-ups" && <FollowUpsPanel parentType="leads" parentId={id} />}
            {tab === "Tasks" && <TasksPanel parentType="leads" parentId={id} />}
            {tab === "Notes" && <NotesPanel notable="leads" id={id} />}
            {tab === "Documents" && <LeadDocuments leadId={id} />}
          </div>
        </div>
      </div>

      <SendMessageDrawer
        open={!!messageChannel}
        lead={lead}
        channel={messageChannel}
        onClose={() => setMessageChannel(null)}
      />
    </AppShell>
  )
}

function Detail({ k, v }) {
  // Masked phone numbers (caller role — see PhoneNumberMasker on the backend)
  // arrive with asterisks; block copy and text selection so they can't be
  // exfiltrated from the UI.
  const masked = typeof v === "string" && v.includes("*")
  return (
    <div>
      <dt className="text-[11px] text-slate-400">{k}</dt>
      <dd
        className={`text-slate-700 ${masked ? "select-none" : ""}`}
        onCopy={masked ? (e) => e.preventDefault() : undefined}
      >
        {v || "—"}
      </dd>
    </div>
  )
}

function Empty({ label, icon: Icon }) {
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
      {Icon && <Icon className="h-5 w-5" />} {label}
    </div>
  )
}
